"use client";
import { SignInButton, SignOutButton, useUser } from "@clerk/nextjs";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";

import SolecitLogo from "@/../public/png_files/SolecitLogo.png";
import { css } from "@/../styled-system/css";
import ChevronRight from "@/assets/svg/chevron-right.svg";
import CircleHelp from "@/assets/svg/circle-help.svg";
import DollarSign from "@/assets/svg/dollar-sign.svg";
import Handshake from "@/assets/svg/handshake.svg";
import Login from "@/assets/svg/log-in.svg";
import Logout from "@/assets/svg/logout.svg";
import MessageSquareText from "@/assets/svg/message-square-text.svg";
import Package from "@/assets/svg/package.svg";
import Shirt from "@/assets/svg/shirt.svg";
import Store from "@/assets/svg/store.svg";
import UserPen from "@/assets/svg/user-pen.svg";
import UserIcon from "@/assets/svg/user.svg";
import Cross from "@/assets/svg/x.svg";

import AccountAvatar from "../Common/AccountAvatar";
import ProfileFooterSkeleton from "../Skeleton/ProfileFooterSkeleton";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../ui/Collapsible";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from "../ui/Sidebar";

// Menu items.
const sidebarItems = [
  {
    title: "Catalog",
    url: "/catalog",
    icon: Store,
  },
  {
    title: "Pricing",
    url: "/pricing",
    icon: DollarSign,
  },
  {
    title: "Contact Us",
    url: "/contact-us",
    icon: MessageSquareText,
  },
  {
    title: "FAQs",
    url: "/faqs",
    icon: CircleHelp,
  },
  {
    title: "About Us",
    url: "/about-us",
    icon: Handshake,
  },
];

const accountItems = [
  {
    title: "Profile",
    url: "/profile",
    icon: UserPen,
  },
  {
    title: "Digital Closet",
    url: "/digital-closet",
    icon: Shirt,
  },
  {
    title: "Orders",
    url: "/order-history",
    icon: Package,
  },
];

const sidebarCloseClass = css({
  cursor: "pointer",
  rounded: "sm",
  h: "28px",
  w: "28px",
  opacity: "0.7",
  transition: "opacity",
  _hover: {
    opacity: "1",
  },
  _disabled: {
    pointerEvents: "none",
  },
});

const profileButtonClass = css({
  display: "inline-flex",
  alignItems: "center",
  gap: "2",
  p: "2.5",
  rounded: "lg",
  borderWidth: "1px",
  borderColor: "neutral.700",
  verticalAlign: "middle",
  cursor: "pointer",
  _hover: { backgroundColor: "neutral.700" },
});

function AppSidebar() {
  const { isSignedIn: isSignedInClerk, user, isLoaded } = useUser();

  const [isSignedIn, setIsSignedIn] = useState<boolean | undefined>(
    !user ? false : true,
  );

  const [userName, setUserName] = useState(user?.fullName || "");
  const [userImageUrl, setUserImageUrl] = useState(user?.imageUrl || "");
  const [userEmailAddress, setUserEmailAddress] = useState(
    user?.emailAddresses[0].emailAddress || "",
  );

  useEffect(() => {
    setIsSignedIn(isSignedInClerk);
    setUserName(user?.fullName || "");
    setUserImageUrl(user?.imageUrl || "");
    setUserEmailAddress(user?.emailAddresses[0].emailAddress || "");
  }, [isSignedInClerk, user]);

  const { toggleSidebar } = useSidebar();
  return (
    <Sidebar>
      <SidebarHeader>
        <Link href="/" prefetch onClick={toggleSidebar}>
          <Image
            unoptimized
            loading="lazy"
            src={SolecitLogo}
            width={125}
            height={24.41}
            className={css({
              filter:
                "invert(100%) sepia(16%) saturate(7463%) hue-rotate(222deg) brightness(119%) contrast(115%)",
            })}
            alt="Solecit Logo"
          />
        </Link>
        <button onClick={toggleSidebar} className={sidebarCloseClass}>
          <Cross width="24px" stroke="white" />
          <span className={css({ srOnly: true })}>Close Sidebar</span>
        </button>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarMenu>
            {sidebarItems.map((item) => (
              <SidebarMenuItem key={item.title}>
                <SidebarMenuButton asChild>
                  <Link href={item.url} onClick={toggleSidebar} prefetch>
                    <item.icon />
                    <span>{item.title}</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            ))}
            {isSignedInClerk && (
              <Collapsible className="group collapsible">
                <SidebarMenuItem>
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton>
                      <UserIcon />
                      <span>Account</span>
                      <ChevronRight
                        className={css({
                          ml: "auto",
                          transitionProperty: "rotate",
                          transitionTimingFunction: "ease-in-out",
                          transitionDuration: "150ms",
                          ".group:is([data-state=open]) &": {
                            rotate: "90deg",
                          },
                        })}
                        width="16px"
                      />
                    </SidebarMenuButton>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <SidebarMenuSub>
                      {accountItems.map((item) => (
                        <SidebarMenuSubItem key={item.title}>
                          <SidebarMenuSubButton asChild>
                            <Link
                              href={item.url}
                              onClick={toggleSidebar}
                              prefetch
                            >
                              <item.icon />
                              <span>{item.title}</span>
                            </Link>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      ))}
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </SidebarMenuItem>
              </Collapsible>
            )}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter>
        <ProfileFooter
          accountImgUrl={userImageUrl}
          emailAddress={userEmailAddress}
          fullName={userName}
          isSignedIn={isSignedIn}
          isLoaded={isLoaded}
        />
      </SidebarFooter>
    </Sidebar>
  );
}

interface ProfileFooterProps {
  accountImgUrl?: string;
  fullName?: string;
  emailAddress?: string;
  isSignedIn: boolean | undefined;
  isLoaded: boolean;
}

function ProfileFooter({
  accountImgUrl,
  fullName,
  emailAddress,
  isSignedIn,
  isLoaded,
}: ProfileFooterProps) {
  return (
    <>
      {isSignedIn ? (
        isLoaded ? (
          <>
            <div className={css({ maxWidth: "52px" })}>
              <AccountAvatar src={accountImgUrl} width="52px" />
            </div>
            <div className={css({ width: "100%", minWidth: "0" })}>
              <div
                className={css({
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  color: "white",
                  lineHeight: "none",
                })}
              >
                <span
                  className={css({
                    fontSize: "xl",
                    fontWeight: "medium",
                  })}
                >
                  {fullName}
                </span>
              </div>
              <div
                className={css({
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  color: "white/50",
                  lineHeight: "tight",
                })}
              >
                <span
                  className={css({
                    fontSize: "sm",
                  })}
                >
                  {emailAddress}
                </span>
              </div>
            </div>
            <SignOutButton>
              <button className={profileButtonClass}>
                <Logout width="1rem" stroke="white" />
                <span className={css({ srOnly: true })}>Logout</span>
              </button>
            </SignOutButton>
          </>
        ) : (
          <ProfileFooterSkeleton />
        )
      ) : (
        <SignInButton>
          <button className={profileButtonClass}>
            <Login width="1rem" stroke="white" />
            <span
              className={css({
                fontWeight: "semibold",
                fontSize: "sm",
                lineHeight: "tight",
                color: "white",
              })}
            >
              Sign In
            </span>
          </button>
        </SignInButton>
      )}
    </>
  );
}

export default AppSidebar;
